import React from 'react';
import { MapPin, BadgeCheck, ShieldOff } from '../icons';
import { Link, useNavigate } from 'react-router-dom';
import StarRating from './StarRating';
import { importGooglePlace } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { vendorImage, categoryImage } from '../utils/categoryImages';

function VendorCard({ vendor, isAdmin, onVerify, onUnverify, verifyingId }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [importing, setImporting] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const priceLabels = {
    budget: `रु ${t('vendorCard.budgetFriendly')}`,
    moderate: `रु रु ${t('vendorCard.moderate')}`,
    premium: `रु रु रु ${t('vendorCard.premium')}`,
    luxury: `रु रु रु रु ${t('vendorCard.luxury')}`,
  };

  const isGoogleLive = vendor.source === 'google_live';

  const handleGoogleLiveClick = async () => {
    if (importing) return;
    setImporting(true);
    try {
      const res = await importGooglePlace(vendor.google_place_id, vendor.category_slug);
      if (res.data?.slug) navigate(`/vendor/${res.data.slug}`);
    } catch {
      // Fallback: open Google Maps if import fails
      const q = encodeURIComponent(vendor.business_name + ' ' + (vendor.city || ''));
      window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank');
    } finally {
      setImporting(false);
    }
  };

  const CardWrapper = isGoogleLive
    ? ({ children }) => (
        <div className="vendor-card" style={{ cursor: importing ? 'wait' : 'pointer', color: 'inherit', opacity: importing ? 0.7 : 1, transition: 'opacity 0.2s' }}
          onClick={handleGoogleLiveClick}>
          {children}
        </div>
      )
    : ({ children }) => (
        <Link to={`/vendor/${vendor.slug}`} className="vendor-card">
          {children}
        </Link>
      );

  return (
    <div className="vendor-card-wrapper" style={{ position: 'relative' }}>
      <CardWrapper>
        <div className="vendor-card-image">
          {!imageLoaded && <div className="vendor-card-shimmer" />}
          <img
            src={vendorImage(vendor, 400, 260)}
            alt={vendor.business_name}
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              const fallback = categoryImage(vendor.category_slug, 400, 260);
              if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
              else setImageLoaded(true);
            }}
            style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
          />
        </div>
        <div className="vendor-card-body">
          {vendor.is_verified ? (
            <span className="vendor-trust-badge trusted"><BadgeCheck size={12} /> {t('vendorCard.trustedVerified')}</span>
          ) : (
            <span className="vendor-trust-badge unverified"><ShieldOff size={12} /> {t('vendorCard.unverified')}</span>
          )}
          <h3 className="vendor-card-name">{vendor.business_name}</h3>
          <div className="vendor-card-rating">
            <StarRating rating={parseFloat(vendor.rating)} />
            <span className="rating-text">{vendor.rating} ({vendor.review_count} {t('vendorCard.reviews')})</span>
          </div>
          <div className="vendor-card-location">
            <MapPin size={14} />
            <span>{vendor.city}{vendor.district && vendor.city !== vendor.district ? `, ${vendor.district}` : ''}</span>
          </div>
          {vendor.is_verified && vendor.price_range && (
            <div className="vendor-card-price">
              {priceLabels[vendor.price_range] || vendor.price_range}
            </div>
          )}
          {vendor.min_price_npr && (
            <div className="vendor-card-price-range">
              NPR {Number(vendor.min_price_npr).toLocaleString()} - {Number(vendor.max_price_npr).toLocaleString()}
            </div>
          )}
        </div>
      </CardWrapper>

      {isAdmin && !vendor.is_verified && onVerify && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVerify(vendor.id); }}
          disabled={verifyingId === vendor.id}
          style={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
            background: '#16a34a',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            zIndex: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            opacity: verifyingId === vendor.id ? 0.6 : 1,
          }}
        >
          <BadgeCheck size={14} /> {verifyingId === vendor.id ? t('vendorCard.verifying') : t('vendorCard.verify')}
        </button>
      )}
      {isAdmin && vendor.is_verified && onUnverify && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onUnverify(vendor.id); }}
          disabled={verifyingId === vendor.id}
          style={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
            background: '#dc2626',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            zIndex: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            opacity: verifyingId === vendor.id ? 0.6 : 1,
          }}
        >
          <ShieldOff size={14} /> {verifyingId === vendor.id ? t('vendorCard.removing') : t('vendorCard.unverify')}
        </button>
      )}
    </div>
  );
}

export default VendorCard;
